/**
 * @module flow-list/components/cells
 *
 * Shared presentational renderers for flow rows. Used by both the table view
 * (DataTable column cells) and the grouped tag-section cards so the two views
 * render flows identically.
 */
import React from 'react';
import {
  Avatar,
  Button,
  Menu,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
  Spinner,
  StatusTimeline,
  TagBadge,
  Text,
  Tooltip,
  TooltipTrigger,
  runBarColor,
  useTheme,
  failHatchStyle,
  type StatusTimelineEntry, badgeVariantToTagColor } from '@chatsdet/ui';
import {
  Archive,
  ChatCircleDots,
  CheckCircle,
  GitFork,
  DotsThreeVertical,
  Pause,
  PauseCircle,
  PencilSimple,
  Play,
  ProhibitInset,
  Question,
  RocketLaunch,
  SealCheck,
  ShieldCheck,
  Shuffle,
  Star,
  Trash,
  Warning,
  WarningCircle,
  Wrench,
  XCircle,
  type Icon as PhosphorIconType,
} from '@phosphor-icons/react';
import { WORKFLOW_HEALTH_LABEL, type WorkflowHealth, type WorkflowManagementStatus } from '@chatsdet/types';
import type { FailureDiagnosticCategory } from '@chatsdet/types/issues';
import {
  CATEGORY_ICONS,
  CATEGORY_LABELS,
} from '../../../components/issues/diagnosticDisplay';
import type { Tag } from '../../../../hooks/useTags';
import { isWorkflowEligible } from '../../../../hooks/useWorkflows';
import type {
  WorkflowListItemWithDetails,
  WorkflowPromotion,
  WorkflowStatus,
} from '../../../../hooks/useWorkflows';
import { formatUpdatedAt } from '../../FlowDesigner/utils';
import { formatRelativeTime } from '../../FlowDesigner/runs/run-formatters';
import { formatDurationMs } from '../../../../lib/formatDuration';
import { formatWorkflowStatus, workflowStatusBadgeVariant } from '../utils';
import type { OpenWorkflowIssueSummary } from '../types';
import { issueHref } from '../../../../utils/issueLinks';

// Health icon shown next to the recent-runs strip. Colors follow the
// run palette (pass = green, fail = burnt orange, flaky = yellow). Colors read
// the status tokens so light/dark stay in step; the icon shape carries the signal.
export const WORKFLOW_HEALTH_META: Record<
  WorkflowHealth,
  { Icon: PhosphorIconType; color: string; label: string; description: string }
> = {
  unverified: {
    Icon: Question,
    color: '#9ca3af',
    label: WORKFLOW_HEALTH_LABEL.unverified,
    description: 'No completed test runs yet.',
  },
  never_passed: {
    Icon: XCircle,
    color: 'hsl(var(--status-failed))',
    label: WORKFLOW_HEALTH_LABEL.never_passed,
    description: 'Has run, but never had a passing run.',
  },
  passing: {
    Icon: CheckCircle,
    color: 'hsl(var(--status-success))',
    label: WORKFLOW_HEALTH_LABEL.passing,
    description: 'Recent runs are all passing.',
  },
  reliable: {
    Icon: ShieldCheck,
    color: 'hsl(var(--status-success))',
    label: WORKFLOW_HEALTH_LABEL.reliable,
    description: 'Enough consecutive clean passes to be considered stable.',
  },
  flaky: {
    // Shuffle = results flip between pass and fail; yellow = intermittent,
    // distinct from the fail-orange bars beside it.
    Icon: Shuffle,
    color: 'hsl(var(--status-flaky))',
    label: WORKFLOW_HEALTH_LABEL.flaky,
    description: 'Recent runs mix passes and failures.',
  },
  failing: {
    // Circle-warning = currently failing but still running, distinct from the octagon-ish
    // Warning used for the harsher `degraded` (auto-skipped) state.
    Icon: WarningCircle,
    color: 'hsl(var(--status-failed))',
    label: WORKFLOW_HEALTH_LABEL.failing,
    description: 'Recent runs are failing — still running in suites.',
  },
  degraded: {
    Icon: Warning,
    color: 'hsl(var(--status-failed))',
    label: WORKFLOW_HEALTH_LABEL.degraded,
    description:
      'Repeated failures — skipped in suite runs until edited, re-enabled, or included via "Re-enable degraded flows" when starting a run.',
  },
};

// Approval-axis icon (status): did a human verify this tests the right thing?
// Mirrors the health-icon pattern; positive = accent (an approval state, not a
// test result, so it uses the primary azure rather than the pass green).
export const WORKFLOW_APPROVAL_META: Record<
  'approved' | 'draft',
  { Icon: PhosphorIconType; color: string; label: string; description: string }
> = {
  approved: {
    Icon: SealCheck,
    color: 'hsl(var(--primary))',
    label: 'Approved',
    description: 'A human verified this test checks the right thing.',
  },
  draft: {
    Icon: PencilSimple,
    color: '#9ca3af',
    label: 'Draft',
    description: 'Not yet approved — no one has signed off on this spec.',
  },
};

// Promotion-axis icon (eligibility flag OFF): does the flow run when test suites
// run? `null` (never promoted) intentionally renders NOTHING — paused is a
// post-promotion state, not a default.
export const WORKFLOW_PROMOTION_META: Record<
  'promoted' | 'paused',
  { Icon: PhosphorIconType; color: string; label: string; description: string }
> = {
  promoted: {
    Icon: RocketLaunch,
    color: 'hsl(var(--primary))',
    label: 'Promoted',
    description: 'Runs whenever your test suites run.',
  },
  paused: {
    Icon: Pause,
    color: 'hsl(var(--status-failed))',
    label: 'Paused',
    description: 'Excluded from suite runs until re-promoted.',
  },
};

// Suite-inclusion axis (eligibility flag ON) — fully *derived* (no stored
// promotion column). A flow is "eligible" once it is approved AND has passed at
// least once; pausing is an orthogonal manual toggle that excludes an
// otherwise-eligible flow. Positive = accent (primary azure — not a test
// result), excluded = orange.
export const WORKFLOW_ELIGIBILITY_META: Record<
  'eligible' | 'paused',
  { Icon: PhosphorIconType; color: string; label: string; description: string }
> = {
  eligible: {
    Icon: RocketLaunch,
    color: 'hsl(var(--primary))',
    label: 'Eligible',
    description: 'Approved and has passed — runs whenever your test suites run.',
  },
  paused: {
    Icon: Pause,
    color: 'hsl(var(--status-failed))',
    label: 'Paused',
    description: 'Excluded from suite runs until resumed.',
  },
};

// Manual lifecycle axis (distinct from approval/health) — `active` renders
// nothing (it's the default, neutral state); every other value gets an icon.
export const WORKFLOW_MANAGEMENT_STATUS_META: Record<
  Exclude<WorkflowManagementStatus, 'active'>,
  { Icon: PhosphorIconType; color: string; label: string; description: string }
> = {
  paused: {
    Icon: PauseCircle,
    color: 'hsl(var(--status-failed))',
    label: 'Paused',
    description: 'Manually taken out of suite runs — temporary, expected to resume.',
  },
  blocked: {
    Icon: ProhibitInset,
    color: '#dc2626',
    label: 'Blocked',
    description: "Can't meaningfully run/pass — e.g. waiting on a bug fix or ticket.",
  },
  under_construction: {
    Icon: Wrench,
    color: '#9ca3af',
    label: 'Under construction',
    description: 'Being actively built/reworked — not ready to run.',
  },
  deprecated: {
    Icon: Archive,
    color: '#9ca3af',
    label: 'Deprecated',
    description: 'Kept for history but no longer maintained or run.',
  },
};

/** Icon + hover tooltip used across the lifecycle cell. */
function LifecycleIconTooltip({
  meta,
  ariaLabel,
}: {
  meta: { Icon: PhosphorIconType; color: string; label: string; description: string };
  ariaLabel: string;
}) {
  return (
    <TooltipTrigger delay={0} offset={8} placement="top">
      <span className="inline-flex cursor-default" aria-label={ariaLabel}>
        <meta.Icon size={15} weight="fill" style={{ color: meta.color }} />
      </span>
      <Tooltip variant="light" className="max-w-xs px-3 py-2 text-left">
        <div className="font-medium">{meta.label}</div>
        <div className="text-muted-foreground">{meta.description}</div>
      </Tooltip>
    </TooltipTrigger>
  );
}

/**
 * Lifecycle cell: approval icon + suite-inclusion icons, with tooltips.
 *
 * - Flag ON (eligibility model): derived eligibility ({@link isWorkflowEligible})
 *   + a "hasn't passed yet" hint + paused, all orthogonal.
 * - Flag OFF (promotion model): the stored `promotion` axis (promoted/paused).
 *
 * System statuses (generating/building/diagnostic/deleted) keep the legacy
 * badge — they aren't a settled approval state.
 */
export function FlowLifecycleIcons({
  item,
  eligibilityModel,
}: {
  item: {
    status: WorkflowStatus;
    firstPassedAt?: string | null;
    pausedAt?: string | null;
    promotion?: WorkflowPromotion;
    managementStatus?: WorkflowManagementStatus;
    /** Set only while a cache-convergence run parked on this flow awaits a feedback answer. */
    pendingFeedbackRequestId?: string | null;
  };
  eligibilityModel: boolean;
}) {
  const { status } = item;
  // Manual lifecycle axis (paused/blocked/under_construction/deprecated) — orthogonal
  // to approval/eligibility, so it's appended after every branch below. `active`
  // (the default) and `paused` (redundant with the eligibility/promotion "Paused"
  // icon above) render nothing here.
  const managementMeta =
    item.managementStatus &&
    item.managementStatus !== 'active' &&
    item.managementStatus !== 'paused'
      ? WORKFLOW_MANAGEMENT_STATUS_META[item.managementStatus]
      : null;
  const managementIcon = managementMeta && (
    <LifecycleIconTooltip meta={managementMeta} ariaLabel={`Flow status: ${managementMeta.label}`} />
  );

  // Auto-maintain feedback loop (spec `specs/pending/auto-maintain.md`, Decisions
  // 11-12) — orthogonal to every other axis, so it's appended after `managementIcon`
  // in every branch below, same as that icon.
  const feedbackIcon = item.pendingFeedbackRequestId && (
    <LifecycleIconTooltip
      meta={{
        Icon: ChatCircleDots,
        color: '#eab308',
        label: 'Waiting for feedback',
        description: "The maintenance agent has a question — it's paused until you answer.",
      }}
      ariaLabel="Waiting for feedback from the maintenance agent"
    />
  );

  if (status !== 'draft' && status !== 'approved') {
    return (
      <span className="inline-flex items-center gap-1.5">
        <FlowStatusBadge status={status} />
        {managementIcon}
        {feedbackIcon}
      </span>
    );
  }
  const approvalMeta = WORKFLOW_APPROVAL_META[status];

  if (!eligibilityModel) {
    // Promotion model (flag OFF): approval icon + (when set) promotion icon.
    const promotionMeta =
      item.promotion === 'promoted' || item.promotion === 'paused'
        ? WORKFLOW_PROMOTION_META[item.promotion]
        : null;
    return (
      <span className="inline-flex items-center gap-1.5">
        <LifecycleIconTooltip meta={approvalMeta} ariaLabel={`Approval: ${approvalMeta.label}`} />
        {promotionMeta && (
          <LifecycleIconTooltip
            meta={promotionMeta}
            ariaLabel={`Promotion: ${promotionMeta.label}`}
          />
        )}
        {managementIcon}
        {feedbackIcon}
      </span>
    );
  }

  const eligible = isWorkflowEligible(item);
  const paused = item.pausedAt != null;
  // Ineligible-but-approved: surface *why* it isn't in suites yet. Draft flows
  // already read "needs approval" from the approval icon, so we don't repeat it.
  const notPassedYet = status === 'approved' && item.firstPassedAt == null;
  return (
    <span className="inline-flex items-center gap-1.5">
      <LifecycleIconTooltip meta={approvalMeta} ariaLabel={`Approval: ${approvalMeta.label}`} />
      {eligible && (
        <LifecycleIconTooltip
          meta={WORKFLOW_ELIGIBILITY_META.eligible}
          ariaLabel={`Eligibility: ${WORKFLOW_ELIGIBILITY_META.eligible.label}`}
        />
      )}
      {notPassedYet && (
        <LifecycleIconTooltip
          meta={{
            Icon: Question,
            color: '#9ca3af',
            label: "Hasn't passed yet",
            description: 'Approved, but runs in suites only after it passes once.',
          }}
          ariaLabel="Eligibility: Hasn't passed yet"
        />
      )}
      {paused && (
        <LifecycleIconTooltip
          meta={WORKFLOW_ELIGIBILITY_META.paused}
          ariaLabel={`Eligibility: ${WORKFLOW_ELIGIBILITY_META.paused.label}`}
        />
      )}
      {managementIcon}
      {feedbackIcon}
    </span>
  );
}

// Map StatusTimeline tone → run-strip result
export const toneToResult = (
  tone: StatusTimelineEntry['tone']
): 'pass' | 'fail' | 'running' | 'skip' | 'idle' => {
  if (tone === 'success' || tone === 'info') return 'pass';
  if (tone === 'danger') return 'fail';
  if (tone === 'warning') return 'running';
  if (tone === 'muted') return 'skip';
  return 'idle';
};

export type LatestRunInfo = {
  ts: string;
  tone: StatusTimelineEntry['tone'];
  durationMs: number | null;
};

/**
 * Favorite star toggle. Amber when starred (favorites are neither pass/fail
 * nor destructive, so neither the green/orange run palette nor red applies).
 * Stops propagation so it composes with clickable rows/cards.
 */
export function FavoriteStar({
  isFavorite,
  onToggle,
  size = 15,
}: {
  isFavorite: boolean;
  onToggle: () => void;
  size?: number;
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
      aria-pressed={isFavorite}
      className="inline-flex items-center justify-center p-1 rounded hover:bg-muted/50 transition-colors"
    >
      <Star
        size={size}
        weight={isFavorite ? 'fill' : 'regular'}
        className={
          isFavorite ? 'text-amber-400' : 'text-muted-foreground/40 hover:text-muted-foreground'
        }
      />
    </button>
  );
}

export function FlowNameCell({
  workflow,
  isBuilding,
  isNew,
}: {
  workflow: WorkflowListItemWithDetails;
  isBuilding: boolean;
  isNew: boolean;
}) {
  return (
    <div className="flex items-center gap-2.5 min-w-0">
      <Text
        weight="medium"
        className="truncate cofactor:text-[13.5px]"
      >
        {workflow.name || 'Untitled Workflow'}
      </Text>
      {isBuilding && (
        <TagBadge
          name="Building"
          color="blue"
          size="sm"
          icon={<Spinner size="xs" />}
          className="animate-pulse"
        />
      )}
      {isNew && <TagBadge name="New" color="blue" hideDot className="text-[10px]" />}
    </div>
  );
}

export function FlowStatusBadge({ status }: { status: WorkflowStatus }) {
  const label = formatWorkflowStatus(status);
  const tone: 'draft' | 'approved' | 'archived' =
    status === 'deleted' ? 'archived' : status === 'approved' ? 'approved' : 'draft';
  return (
    <>
      <span className="cofactor:hidden">
        <TagBadge
          name={label}
          color={
            badgeVariantToTagColor(workflowStatusBadgeVariant[status] ?? 'secondary')
          }
          hideDot
        />
      </span>
      <span data-component="flow-status" data-tone={tone} className="hidden cofactor:inline-flex">
        <span className="dot" />
        {label}
      </span>
    </>
  );
}

export function FlowTagsCell({ tags }: { tags: Tag[] }) {
  if (!tags.length)
    return (
      <Text size="sm" className="text-muted-foreground">
        —
      </Text>
    );
  const visibleTags = tags.slice(0, 3);
  const hiddenTags = tags.slice(3);
  return (
    <div className="flex flex-wrap gap-1 items-center">
      {visibleTags.map((tag) => (
        <TagBadge key={tag.id} name={tag.name} color={tag.color} size="sm" />
      ))}
      {hiddenTags.length > 0 && (
        <TooltipTrigger delay={0} offset={8} placement="top">
          <span className="inline-flex cursor-default">
            <Text size="xs" className="text-muted-foreground">
              +{hiddenTags.length}
            </Text>
          </span>
          <Tooltip variant="light" className="max-w-xs px-3 py-2 text-left">
            <div className="flex flex-col gap-0.5">
              {hiddenTags.map((tag) => (
                <span key={tag.id}>{tag.name}</span>
              ))}
            </div>
          </Tooltip>
        </TooltipTrigger>
      )}
    </div>
  );
}

/**
 * Compact "N issues" chip shown when a flow has open issues persistently
 * linked to it. Hovering lists each issue's title + shortcode, each linking
 * to the issue drawer (`/issues?issueId=...`, same URL shape as
 * `FailureDiagnosticSection`'s "View Issue" button).
 */
export function FlowOpenIssuesChip({ issues }: { issues: OpenWorkflowIssueSummary[] }) {
  if (issues.length === 0) return null;
  return (
    <TooltipTrigger delay={0} offset={8} placement="top">
      <span className="inline-flex cursor-default">
        <TagBadge
          name={`${issues.length} issue${issues.length === 1 ? '' : 's'}`}
          color="red"
          hideDot
          size="sm"
        />
      </span>
      <Tooltip variant="light" className="max-w-xs px-3 py-2 text-left">
        <div className="flex flex-col gap-1">
          {issues.map((issue) => (
            <a
              key={issue.id}
              href={issueHref(issue.id)}
              className="text-primary hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              <span className="font-mono text-xs text-muted-foreground mr-1">
                {issue.shortcode}
              </span>
              {issue.title}
            </a>
          ))}
        </div>
      </Tooltip>
    </TooltipTrigger>
  );
}

/**
 * Recent-runs strip + health icon (+ cofactor pass-rate %). Both brands render
 * the same 10-run summary; chrome differs (cofactor run-strip vs StatusTimeline).
 */
export function FlowRecentRuns({
  entries,
  health,
  isPageLoading,
  diagnosedCategory = null,
  openIssues,
}: {
  entries: StatusTimelineEntry[];
  health: WorkflowHealth;
  isPageLoading: boolean;
  /**
   * Category of the most recent run — within the current failure streak —
   * that has a completed failure diagnosis. See
   * `findLastDiagnosedCategoryInStreak` in `features/workflows/runTimeline.tsx`.
   * Null renders nothing extra (no diagnosis in the streak, or none failing).
   */
  diagnosedCategory?: FailureDiagnosticCategory | null;
  /** Open issues persistently linked to this flow — renders {@link FlowOpenIssuesChip} when non-empty. */
  openIssues?: OpenWorkflowIssueSummary[];
}) {
  const { config } = useTheme();
  // Pass rate over *terminal* outcomes only — queued/running/canceled entries
  // (muted/warning tones) would deflate the percentage without representing a
  // real verdict, and health classification ignores them too.
  const passCount = entries.filter((e) => e.tone === 'success' || e.tone === 'info').length;
  const failCount = entries.filter((e) => e.tone === 'danger').length;
  const total = passCount + failCount;
  const pct = total > 0 ? Math.round((passCount / total) * 100) : null;
  return (
    <div className="flex w-full items-center gap-2">
      <span className="hidden cofactor:inline-flex gap-[2px] items-center shrink-0">
        {(() => {
          // Match the canary StatusTimeline chronology: newest run on the
          // right, older to the left, empty slots padding the left edge.
          const chronological = entries.slice(0, 10).reverse();
          const emptySlots = Math.max(0, 10 - chronological.length);
          return Array.from({ length: 10 }).map((_, i) => {
            const entry = i >= emptySlots ? chronological[i - emptySlots] : undefined;
            const result = entry ? toneToResult(entry.tone) : 'idle';
            // The brand's run palette (pass = green, running = violet, fail =
            // burnt orange), resolved from the centralized status tokens so it
            // tracks the theme. Fail bars are hatched so they differ from pass
            // without color.
            const barStyle = {
              backgroundColor: runBarColor(result, config.theme),
              ...failHatchStyle(result === 'fail'),
            };
            if (!entry) {
              return (
                <span
                  key={`empty-${i}`}
                  className="inline-block w-[7px] h-[14px] flex-shrink-0"
                  style={barStyle}
                />
              );
            }
            return (
              <TooltipTrigger key={entry.id} delay={0} offset={8} placement="top">
                <span
                  className="inline-block w-[7px] h-[14px] flex-shrink-0 cursor-default"
                  style={barStyle}
                />
                <Tooltip variant="light" className="max-w-sm px-3 py-2 text-left">
                  {entry.tooltipContent}
                </Tooltip>
              </TooltipTrigger>
            );
          });
        })()}
      </span>
      <span className="cofactor:hidden">
        <StatusTimeline
          entries={entries}
          slotCount={10}
          size="sm"
          isPageLoading={isPageLoading}
          showNavigation={false}
        />
      </span>
      {(() => {
        const meta = WORKFLOW_HEALTH_META[health];
        if (!meta) return null;
        return (
          <TooltipTrigger delay={0} offset={8} placement="top">
            <span className="inline-flex cursor-default" aria-label={`Health: ${meta.label}`}>
              <meta.Icon size={14} weight="fill" style={{ color: meta.color }} />
            </span>
            <Tooltip variant="light" className="max-w-xs px-3 py-2 text-left">
              <div className="font-medium">{meta.label}</div>
              <div className="text-muted-foreground">{meta.description}</div>
            </Tooltip>
          </TooltipTrigger>
        );
      })()}
      {diagnosedCategory && (
        <TooltipTrigger delay={0} offset={8} placement="top">
          <span
            className="inline-flex cursor-default text-muted-foreground"
            aria-label={`Diagnosed: ${CATEGORY_LABELS[diagnosedCategory]}`}
          >
            {React.cloneElement(
              CATEGORY_ICONS[diagnosedCategory] as React.ReactElement<{ size?: number }>,
              { size: 14 }
            )}
          </span>
          <Tooltip variant="light" className="max-w-xs px-3 py-2 text-left">
            <div className="font-medium">{CATEGORY_LABELS[diagnosedCategory]}</div>
          </Tooltip>
        </TooltipTrigger>
      )}
      {openIssues && openIssues.length > 0 && <FlowOpenIssuesChip issues={openIssues} />}
      {pct !== null && (
        <span
          className={`hidden cofactor:inline-block shrink-0 ml-auto font-mono text-[11px] tabular-nums w-[34px] text-right ${
            pct < 50 ? 'text-destructive font-medium' : 'text-muted-foreground'
          }`}
        >
          {pct}%
        </span>
      )}
    </div>
  );
}

export function FlowAuthorCell({
  member,
}: {
  member: { displayName: string | null; primaryEmail: string | null } | null | undefined;
}) {
  const name = member?.displayName || member?.primaryEmail || null;
  if (!name)
    return (
      <Text size="sm" className="text-muted-foreground">
        —
      </Text>
    );
  return (
    <Avatar
      name={member?.displayName ?? null}
      email={member?.primaryEmail ?? null}
      size="xs"
    />
  );
}

/** Duration of the last run, right-aligned to sit beside {@link FlowLastRunCell}. */
export function FlowLastRunDurationCell({ durationMs }: { durationMs: number | null }) {
  if (durationMs == null)
    return (
      <Text size="sm" className="text-muted-foreground text-right block">
        —
      </Text>
    );
  return (
    <span className="text-muted-foreground text-right block text-sm cofactor:font-mono cofactor:text-[11px] tabular-nums">
      {formatDurationMs(durationMs)}
    </span>
  );
}

export function FlowLastRunCell({ latest }: { latest: LatestRunInfo | undefined }) {
  if (!latest)
    return (
      <Text size="sm" className="text-muted-foreground text-right block">
        —
      </Text>
    );
  return (
    <TooltipTrigger delay={200}>
      <span className="text-muted-foreground text-right block text-sm cofactor:font-mono cofactor:text-[11px] tabular-nums cursor-default">
        {formatRelativeTime(latest.ts)}
      </span>
      <Tooltip variant="dark">{formatUpdatedAt(latest.ts)}</Tooltip>
    </TooltipTrigger>
  );
}

/**
 * Minimal flow shape the per-flow actions need — satisfied by both the
 * table's full list items and the grouped view's skinny items.
 */
export type FlowActionTarget = Pick<
  WorkflowListItemWithDetails,
  'id' | 'name' | 'status' | 'firstPassedAt' | 'pausedAt' | 'promotion'
>;

/**
 * Callbacks + pending flags for per-flow actions. Owned by FlowList (mutations
 * live there); consumed by both the table's actions column and grouped cards.
 */
export type FlowActionHandlers = {
  onRunTest: (workflow: FlowActionTarget) => void;
  onFork: (workflowId: string) => void;
  /** Approval axis (draft ↔ approved). */
  onSetStatus: (workflow: FlowActionTarget, status: WorkflowStatus) => void;
  /** Eligibility model (flag ON): suite-inclusion pause toggle (orthogonal to derived eligibility). */
  onSetPaused: (workflow: FlowActionTarget, paused: boolean) => void;
  /** Promotion model (flag OFF): suite-inclusion axis. */
  onSetPromotion: (workflow: FlowActionTarget, promotion: 'promoted' | 'paused') => void;
  onArchive: (workflow: FlowActionTarget) => void;
  isRunPending: boolean;
  isStatusPending: boolean;
  isForkPending: boolean;
};

export function FlowActionsMenu({
  workflow,
  handlers,
  eligibilityModel,
}: {
  workflow: FlowActionTarget;
  handlers: FlowActionHandlers;
  eligibilityModel: boolean;
}) {
  return (
    <div onClick={(e) => e.stopPropagation()}>
      <MenuTrigger>
        <Button
          variant="ghost"
          size="sm"
          className="p-2 text-muted-foreground"
          aria-label="Workflow actions"
        >
          <DotsThreeVertical size={16} weight="bold" />
        </Button>
        <Menu
          onAction={(key) => {
            // Don't allow actions while mutations are pending
            if (handlers.isRunPending || handlers.isStatusPending || handlers.isForkPending) {
              return;
            }
            if (key === 'fork') {
              handlers.onFork(workflow.id);
            } else if (key === 'run-test') {
              handlers.onRunTest(workflow);
            } else if (key === 'approve') {
              handlers.onSetStatus(workflow, 'approved');
            } else if (key === 'set-draft') {
              handlers.onSetStatus(workflow, 'draft');
            } else if (key === 'promote') {
              handlers.onSetPromotion(workflow, 'promoted');
            } else if (key === 'pause') {
              // Promotion model pauses via the promotion axis; eligibility model
              // via the standalone pause toggle.
              if (eligibilityModel) {
                handlers.onSetPaused(workflow, true);
              } else {
                handlers.onSetPromotion(workflow, 'paused');
              }
            } else if (key === 'unpause') {
              handlers.onSetPaused(workflow, false);
            } else if (key === 'archive') {
              handlers.onArchive(workflow);
            }
          }}
        >
          {workflow.status === 'draft' && (
            <MenuItem id="approve">
              <SealCheck size={14} className="mr-2" />
              Approve
            </MenuItem>
          )}
          {workflow.status === 'approved' && (
            <MenuItem id="set-draft">
              <PencilSimple size={14} className="mr-2" />
              Set draft
            </MenuItem>
          )}
          {/* Promotion model (flag OFF): Promote / Pause. */}
          {!eligibilityModel && workflow.status === 'approved' && workflow.promotion !== 'promoted' && (
            <MenuItem id="promote">
              <RocketLaunch size={14} className="mr-2" />
              Promote
            </MenuItem>
          )}
          {!eligibilityModel && workflow.promotion === 'promoted' && (
            <MenuItem id="pause">
              <Pause size={14} className="mr-2" />
              Pause
            </MenuItem>
          )}
          {/* Eligibility model (flag ON): Pause / Unpause. */}
          {eligibilityModel && isWorkflowEligible(workflow) && workflow.pausedAt == null && (
            <MenuItem id="pause">
              <Pause size={14} className="mr-2" />
              Pause
            </MenuItem>
          )}
          {eligibilityModel && workflow.pausedAt != null && (
            <MenuItem id="unpause">
              <RocketLaunch size={14} className="mr-2" />
              Unpause
            </MenuItem>
          )}
          <MenuSeparator />
          <MenuItem id="run-test">
            {handlers.isRunPending ? (
              <Spinner size="sm" className="mr-2" />
            ) : (
              <Play size={14} className="mr-2" />
            )}
            {handlers.isRunPending ? 'Starting...' : 'Run test'}
          </MenuItem>
          <MenuItem id="fork">
            {handlers.isForkPending ? (
              <Spinner size="sm" className="mr-2" />
            ) : (
              <GitFork size={14} className="mr-2" />
            )}
            {handlers.isForkPending ? 'Forking...' : 'Fork'}
          </MenuItem>
          <MenuItem id="archive" destructive>
            <Trash size={14} className="mr-2" />
            Archive
          </MenuItem>
        </Menu>
      </MenuTrigger>
    </div>
  );
}
